import Card from "components/Card/Card";
import { ControlledInput } from "components/Card/ControlledInput";
import { MaskedInput, TextInput } from "components/inputs";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import {
  useGetFileByIdQuery,
  usePatchFileByIdMutation,
} from "services/file-api.ts";

type DefenseAttorneyInformationFormProps = {
  fileId?: string;
};

type DefenseAttorney = {
  defenseCounsel: string;
  defenseCounselEmail: string | null;
  defenseCounselFirmName: string | null;
  claimantCounsel: string | null;
  attorneyName: string | null;
  attorneyAddress: string | null;
  attorneyPhone: string | null;
};

const DefenseAttorneyInformationForm: React.FC<
  DefenseAttorneyInformationFormProps
> = ({ fileId }) => {
  const [patchFileById] = usePatchFileByIdMutation();

  const { data: fileData } = useGetFileByIdQuery(
    { id: fileId },
    { skip: !fileId },
  );

  const handleSaveFile = useCallback(
    async (field: string, value: string | number) => {
      await patchFileById({
        id: fileId,
        data: {
          [field]: value,
        },
      });
    },
    [patchFileById, fileId],
  );

  const {
    control,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    if (!fileId) return;

    const defenseAttorney: DefenseAttorney = {
      defenseCounsel: fileData?.defense_counsel || "",
      defenseCounselEmail: fileData?.defense_counsel_email || null,
      defenseCounselFirmName: fileData?.defense_counsel_firm_name || null,
      claimantCounsel: fileData?.claimant_counsel || null,
      attorneyName: fileData?.attorney_name || null,
      attorneyAddress: fileData?.attorney_address || null,
      attorneyPhone: fileData?.attorney_phone || null,
    };
    reset(defenseAttorney);
  }, [fileId, fileData]);

  return (
    <Card
      heading="Defense Attorney Information"
      classNames="future-medical-defense-attorney-information"
    >
      <ControlledInput
        control={control}
        name="defenseCounsel"
        label="Defense Counsel"
        onSave={(value) => handleSaveFile("defense_counsel", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="defenseCounselEmail"
        label="Defense Counsel Email"
        onSave={(value) => handleSaveFile("defense_counsel_email", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="defenseCounselFirmName"
        label="Defense Counsel Firm Name"
        onSave={(value) => handleSaveFile("defense_counsel_firm_name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="claimantCounsel"
        label="Claimant Counsel"
        onSave={(value) => handleSaveFile("claimant_counsel", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="attorneyName"
        label="Attorney Name"
        onSave={(value) => handleSaveFile("attorney_name", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="attorneyAddress"
        label="Attorney Address"
        onSave={(value) => handleSaveFile("attorney_address", value)}
        errors={errors}
        componentType={TextInput}
        readOnly={!fileId}
      />
      <ControlledInput
        control={control}
        name="attorneyPhone"
        label="Attorney Phone"
        onSave={(value) => handleSaveFile("attorney_phone", value)}
        errors={errors}
        componentType={MaskedInput}
        mask="phone"
        readOnly={!fileId}
      />
    </Card>
  );
};

export default DefenseAttorneyInformationForm;
